import React from "react";
import CertificateFooter from "./CertificateFooter";

const formatOrdinalDate = (value) => {
  if (!value) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  const day = date.getDate();
  const mod10 = day % 10;
  const mod100 = day % 100;
  let suffix = "th";
  if (mod10 === 1 && mod100 !== 11) suffix = "st";
  else if (mod10 === 2 && mod100 !== 12) suffix = "nd";
  else if (mod10 === 3 && mod100 !== 13) suffix = "rd";
  const month = date.toLocaleDateString("en-US", { month: "long" });
  return `${month} ${String(day).padStart(2, "0")}${suffix}, ${date.getFullYear()}`;
};

const LabelValueRows = ({ rows }) => (
  <table className="cc-exam-meta">
    <tbody>
      {rows.map(([label, value, bold]) => (
        <tr key={label}>
          <th>{label}</th>
          <td className={bold ? "cc-exam-meta__bold" : undefined}>{value}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

/**
 * A4 preview for the Thorough Examination (Mobile Crane) template.
 * Header / sign-off / disclaimer / footer match the Aluminum Truss report.
 */
const ThoroughExaminationDocument = ({ doc }) => {
  if (!doc) return null;
  const assetsBase = doc.assetsBase || "";
  const logos = doc.footerLogos || [];

  return (
    <div className="cc-pdf-page cc-exam-page">
      <div className="cc-pdf-header">
        <img className="cc-pdf-logo" src={`${assetsBase}/logo.jpg`} alt="TUV PAK" />
        <div className="cc-pdf-header-text">
          <strong>TUV PAK INSPEC</strong>
          <span>TECHANICAL UNIQUE VISION PAKISTAN INSPECTION &amp; CERTIFICATION</span>
        </div>
      </div>
      <img className="cc-pdf-rule" src={`${assetsBase}/header-bar.png`} alt="" />

      <div className="cc-exam-title">{doc.report_title || "THOROUGH EXAMINATION REPORT"}</div>

      <LabelValueRows
        rows={[
          ["Report Reference:", doc.report_no],
          ["Activity:", doc.activity],
          ["Client:", doc.client, true],
          ["Location of Inspection:", doc.location],
          ["Inspected by:", doc.inspected_by_name],
        ]}
      />

      <LabelValueRows
        rows={[
          ["Description of Supply:", doc.description_of_supply],
          ["Make/Model:", doc.make_model],
          ["Serial No/Chassis.", doc.serial_no],
          ["Registration Number:", doc.registration_number],
          ["Capacity:", doc.capacity],
          ["Type of Operation:", doc.type_of_operation],
          ["Scope of Survey:", doc.scope_of_survey],
        ]}
      />

      <div className="cc-exam-scope-row">
        <div className="cc-exam-photo">
          {doc.examPhotoUrl ? (
            <img src={doc.examPhotoUrl} alt="Equipment" />
          ) : (
            <span className="cc-exam-photo__empty">Equipment photo</span>
          )}
          <img className="cc-exam-watermark" src={`${assetsBase}/crane-exam-watermark.png`} alt="" />
        </div>
        <div className="cc-exam-scope-text">{doc.scope_of_survey_detail}</div>
      </div>

      <table className="cc-exam-meta cc-exam-operation">
        <tbody>
          <tr>
            <th>Operation Test</th>
            <td>{doc.operation_test}</td>
          </tr>
        </tbody>
      </table>

      <table className="cc-exam-dates">
        <tbody>
          <tr>
            <th>Date of Issuance:</th>
            <td>{formatOrdinalDate(doc.date_of_issuance || doc.issue_date)}</td>
            <th rowSpan={2}>Date of Inspection:</th>
            <td rowSpan={2}>{formatOrdinalDate(doc.date_of_inspection || doc.report_date)}</td>
          </tr>
          <tr>
            <th>Date of Validity:</th>
            <td>{formatOrdinalDate(doc.date_of_validity || doc.expire_date)}</td>
          </tr>
        </tbody>
      </table>

      <p className="cc-exam-ref">
        <b>Reference code &amp; standard:</b> {doc.reference_code}
      </p>
      <p className="cc-exam-ref">
        <b>Recommendations/Subjected to:</b> {doc.recommendations}
      </p>

      <table className="cc-pdf-sign">
        <thead>
          <tr>
            <th>Inspected By</th>
            <th>Approved By</th>
            <th>Company Seal</th>
            <th>QR Code</th>
          </tr>
        </thead>
        <tbody>
          <tr className="cc-pdf-sign-names">
            <td>{doc.inspected_by_name}</td>
            <td>{doc.approved_by_name}</td>
            <td rowSpan={2} className="cc-pdf-sign-seal">
              {doc.sealUrl && <img src={doc.sealUrl} alt="Company seal" />}
            </td>
            <td rowSpan={2} className="cc-pdf-sign-qr">
              {doc.qrDataUrl && <img src={doc.qrDataUrl} alt="QR code" />}
            </td>
          </tr>
          <tr className="cc-pdf-sign-images">
            <td>{doc.inspectedSignatureUrl && <img src={doc.inspectedSignatureUrl} alt="Inspector signature" />}</td>
            <td>{doc.approvedSignatureUrl && <img src={doc.approvedSignatureUrl} alt="Approver signature" />}</td>
          </tr>
        </tbody>
      </table>

      <div className="cc-pdf-disclaimer">
        <p>{doc.disclaimer_text}</p>
        <b>{doc.end_of_report_text}</b>
      </div>

      <CertificateFooter
        assetsBase={assetsBase}
        logos={logos}
        website={doc.footer_website}
        email={doc.footer_email}
        gmail={doc.footer_gmail}
      />
    </div>
  );
};

export default ThoroughExaminationDocument;
